/**
 * useSignalStream
 *
 * Listens for `signals` events via the multiplexed WS singleton (wsClient → /ws).
 *
 * The backend publishes to Redis `signals:approved` which is forwarded to all
 * subscribed clients as `{ type: 'event', topic: 'signals', data: { ... } }`.
 *
 * Public API is identical to the previous version — no consumer code changes needed.
 */
import { useEffect, useRef, useCallback } from 'react';
import type { Signal } from '@trading/shared';
import { logger } from '../lib/logger.js';
import { wsClient } from '../lib/ws/client.js';

interface UseSignalStreamOptions {
  onNewSignal: (signal: Signal) => void;
  autoReconnect?: boolean;
}

export function useSignalStream({ onNewSignal }: UseSignalStreamOptions) {
  const onNewSignalRef = useRef(onNewSignal);

  useEffect(() => {
    onNewSignalRef.current = onNewSignal;
  }, [onNewSignal]);

  // Stable handler with stable identity — subscription persists across renders
  const handlerRef = useRef((data: unknown) => {
    try {
      // Backend sends { type: 'new_signal', signal: Signal } inside data
      const inner = data as { signal?: Signal } | null | undefined;
      const signal = inner?.signal;
      if (signal) {
        logger.info('[SignalStream] New signal received:', signal.id);
        onNewSignalRef.current(signal);
      } else {
        logger.warn('[SignalStream] signals event missing signal field', data);
      }
    } catch (err) {
      logger.error('[SignalStream] Handler error:', err);
    }
  });

  const subscribe = useCallback(() => {
    return wsClient.subscribe('signals', handlerRef.current);
  }, []);

  useEffect(() => {
    const unsub = subscribe();
    return unsub;
  }, [subscribe]);

  return {} as const;
}

export default useSignalStream;
